import React, { useRef } from 'react';
import Field from '../Field';
import { ACCEPTED_FILE_TYPES } from '../../lib/fileLimits';
import { EXPENSE_ATTACHMENTS_MAX } from '../../domain/expenses';
import { fileLinkMessage } from '../../domain/fileLinks';
import { resourceMessage } from '../../domain/resources';
import UploadProgressList from '../UploadProgress';
import s from './EntryForm.module.css';

// The receipts on an entry: choose them, view one, or take one off.
//
// A list with a card each (#104), as an MMS expense takes many attachments,
// listed as separate cards under the paperclip; the chooser stays under it.
//
// The chooser is `multiple`, which is ours: whether MMS's picker takes several
// at once is unchecked, and `attach` takes a list either way.
//
// A receipt on its way up is a row under the cards (#612) — the upload row
// every upload site draws (src/components/UploadProgress.jsx): its bar and
// amount, × to cancel, and a failed one's sentence with Try again.
export default function AttachmentField({
  attachments = [], busy, error, onPick, onRemove, onView, onCopyLink = null, notice = null,
  uploads = [], onRetryUpload = null, onCancelUpload = null, onDismissUpload = null,
}) {
  const inputRef = useRef(null);
  const full = attachments.length >= EXPENSE_ATTACHMENTS_MAX;

  return (
    <div className={s.section}>
      <Field
        label={attachments.length > 1 ? 'Attachments' : 'Attachment'}
        hint="Optional — photos, PDFs or other files of the receipt"
      >
        {attachments.length ? (
          <div className={s.attachmentList}>
            {attachments.map((attachment, i) => (
              // Keyed on the path, which is unique per object: every upload
              // mints its own 160-bit filename. An index key would carry a
              // card's state onto its neighbour when one above it is removed.
              <div className={s.attachment} key={attachment.path || i}>
                <span className={s.attachmentName}>{attachment.name}</span>
                <button
                  type="button"
                  className={`${s.attachmentAction} ${s.actionPrimary}`}
                  onClick={() => onView(attachment.path)}
                >
                  View
                </button>
                {/* The thirty-day link itself (#515), for a teacher who wants
                    to send it. What a tab opened by View ends up showing is the
                    hour-long storage URL the link redirects to, which is not
                    worth copying. */}
                {onCopyLink ? (
                  <button
                    type="button"
                    className={`${s.attachmentAction} ${s.actionPrimary}`}
                    onClick={() => onCopyLink(attachment.path)}
                  >
                    {resourceMessage('copy_link')}
                  </button>
                ) : null}
                {/* The stored object is left alone here: the entry may not be
                    saved at all, and deleting on "remove" would destroy the
                    receipt of an edit the teacher then cancels.
                    useExpenseEntryForm remembers every object the session
                    uploaded and takes the orphans away once a row has been
                    written or the form abandoned (#94), comparing the whole list
                    on both sides (#104), so removing one of three sweeps exactly
                    that one. */}
                <button
                  type="button"
                  className={`${s.attachmentAction} ${s.actionMuted}`}
                  aria-label={`Remove ${attachment.name}`}
                  onClick={() => onRemove(i)}
                >
                  Remove
                </button>
              </div>
            ))}
            {/* View opens each file through a link that works for thirty
                days, and anyone handed a link is told so (#497). The sentence
                is the core's (`file_links.rs`, `app_note`), shared with both
                phones. What the tab ends up showing is the hour-long storage
                URL the link redirects to, not the link itself, which is why
                Copy link is beside it (#515). */}
            <p className={s.linkNote}>{fileLinkMessage('app_note')}</p>
            {notice ? <p className={notice.kind === 'error' ? s.error : s.linkNote} role="status">{notice.text}</p> : null}
          </div>
        ) : null}

        <UploadProgressList
          items={uploads}
          onRetry={onRetryUpload}
          onCancel={onCancelUpload}
          onDismiss={onDismissUpload}
        />

        {/* Under the list rather than instead of it, so a second receipt is one
            press away. Disabled at the ceiling, where the refusal a teacher can
            act on is `ATTACHMENTS_FULL` under the field — the button being dead
            is not the message, for the reason `entryErrors` gives about Save. */}
        <button
          type="button"
          className={`${s.input} ${s.chooseFile} ${busy ? s.chooseFileBusy : ''}`}
          disabled={busy || full}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? 'Uploading…' : (attachments.length ? 'Add another file…' : 'Choose a file…')}
        </button>
      </Field>

      <input
        ref={inputRef}
        type="file"
        multiple
        /* The studio file service's list (#96): a receipt is a file and takes
           the same types, as MMS accepts a card statement or an emailed
           spreadsheet. This is the store's real answer, so nothing is greyed
           out that the upload would then take. */
        accept={ACCEPTED_FILE_TYPES}
        style={{ display: 'none' }}
        onChange={(e) => {
          // Cleared before the handler runs, so picking the same file twice in
          // a row still fires a change event — and so the element is not left
          // holding a reference to files the form has moved on from.
          const files = Array.from(e.target.files || []);
          e.target.value = '';
          onPick(files);
        }}
      />
      {error ? <div className={s.error}>{error}</div> : null}
    </div>
  );
}
